// useDragToClose — swipe-left-to-close handler for the mobile drawer.
//
// Returns DOM event handlers + a translateX value (in pixels) that the
// caller multiplies into the drawer's transform. While the user drags,
// translateX is negative (pulling the drawer leftward off-screen). On
// release: if the user dragged past `threshold` OR flicked fast enough
// (`velocityThreshold` px/ms), we call `onClose`; otherwise the drawer
// snaps back to translateX 0.
//
// Touch + mouse handlers so it works in DevTools mobile emulation too.
//
// Pattern adapted from /opt/4bix-source/src/components/layout/drawer-menu.tsx
// (touch + mouse handlers, lines 88–111). Specifics:
//   - Only intercepts horizontal drags (we don't want to fight scroll).
//   - Re-arms on each open via the `enabled` flag.

import { useCallback, useEffect, useRef, useState } from 'react'

interface Options {
  enabled: boolean
  onClose: () => void
  /** Distance in pixels past which a slow drag triggers close. */
  threshold?: number
  /** Pixels-per-millisecond velocity that triggers close even at small distance. */
  velocityThreshold?: number
}

export function useDragToClose({
  enabled,
  onClose,
  threshold = 100,
  velocityThreshold = 0.5,
}: Options) {
  const [translateX, setTranslateX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startRef = useRef<{ x: number; y: number; t: number } | null>(null)
  const lockedRef = useRef<'horizontal' | 'vertical' | null>(null)

  const reset = useCallback(() => {
    startRef.current = null
    lockedRef.current = null
    setIsDragging(false)
    setTranslateX(0)
  }, [])

  useEffect(() => {
    if (!enabled) reset()
  }, [enabled, reset])

  const start = useCallback(
    (x: number, y: number) => {
      if (!enabled) return
      startRef.current = { x, y, t: Date.now() }
      lockedRef.current = null
      setIsDragging(true)
    },
    [enabled],
  )

  const move = useCallback((x: number, y: number) => {
    if (!startRef.current) return
    const dx = x - startRef.current.x
    const dy = y - startRef.current.y

    // Lock axis on first ~10px of movement so we don't fight vertical scroll.
    if (!lockedRef.current && Math.abs(dx) + Math.abs(dy) > 10) {
      lockedRef.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
    }

    if (lockedRef.current === 'vertical') {
      // Not our gesture — release.
      reset()
      return
    }

    // Only allow leftward drag (closing).
    setTranslateX(Math.min(0, dx))
  }, [reset])

  const end = useCallback(() => {
    if (!startRef.current) return reset()
    const dx = translateX
    const dt = Date.now() - startRef.current.t
    const velocity = Math.abs(dx) / Math.max(dt, 1) // px/ms (positive)
    const shouldClose = -dx > threshold || velocity > velocityThreshold
    if (shouldClose) {
      onClose()
    }
    reset()
  }, [onClose, threshold, velocityThreshold, translateX, reset])

  // Touch handlers
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const t = e.touches[0]
      if (t) start(t.clientX, t.clientY)
    },
    [start],
  )
  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const t = e.touches[0]
      if (t) move(t.clientX, t.clientY)
    },
    [move],
  )
  const onTouchEnd = useCallback(() => end(), [end])

  // Mouse handlers (DevTools, desktop testing). Use window-level listeners while
  // dragging so the gesture doesn't get cancelled if the cursor leaves the drawer.
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => start(e.clientX, e.clientY),
    [start],
  )
  useEffect(() => {
    if (!isDragging) return
    const mm = (e: MouseEvent) => move(e.clientX, e.clientY)
    const mu = () => end()
    window.addEventListener('mousemove', mm)
    window.addEventListener('mouseup', mu)
    return () => {
      window.removeEventListener('mousemove', mm)
      window.removeEventListener('mouseup', mu)
    }
  }, [isDragging, move, end])

  return {
    translateX,
    isDragging,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMouseDown,
    },
  }
}
